import { ReactNode, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { hasAnyPermissionForJob } from '../../constants/permissions';
import ScreenHeader from '../common/screen-header';
import TabBar from '../common/tab-bar';

export interface GroupTab {
  key: string;
  header: string;
  leftIcon?: string; // e.g. 'pi pi-building'
  jobs: string[]; // JOB_NAMES governing this tab (OR); empty = always show
  element: ReactNode; // the EXISTING list component, unchanged
}

interface GroupScreenProps {
  icon: string;
  iconBgColor?: string;
  title: string;
  subtitle?: string;
  tabs: GroupTab[];
  testId?: string;
}

/**
 * Reusable "merged" admin screen — the same shape as the Access Control (RBAC)
 * dashboard: a header, then a card holding a TabView. Each tab embeds an EXISTING
 * list screen unchanged. Tabs are permission-filtered: a tab only renders if the
 * user has any permission on its governing job(s), mirroring the sidebar's own
 * visibility rule. Create/Edit inside each list still navigate to the existing
 * form pages — nothing about the CRUD screens changes.
 *
 * The open tab is addressable as `?tab=<key>`, which is what lets the sidebar
 * list a screen's tabs as navigable children. It is additive: no parameter, or
 * one naming a tab this user cannot see, opens the first visible tab exactly as
 * before. The URL is replaced rather than pushed when switching tabs, so the
 * back button leaves the screen rather than walking its tab strip.
 */
const GroupScreen = ({
  icon,
  iconBgColor = 'bg-indigo-600',
  title,
  subtitle,
  tabs,
  testId,
}: GroupScreenProps) => {
  const permissionKeys = useSelector(
    (s: { auth?: { permissions?: string[] } }) => s.auth?.permissions || []
  );

  const visibleTabs = useMemo(() => {
    const keys = new Set<string>(permissionKeys);
    return tabs.filter(
      (t) => t.jobs.length === 0 || t.jobs.some((j) => hasAnyPermissionForJob(j, keys))
    );
  }, [tabs, permissionKeys]);

  const [params, setParams] = useSearchParams();
  // An unknown or forbidden key falls back to the first visible tab rather than
  // rendering nothing, so a stale bookmark or a renamed key degrades quietly.
  const wanted = visibleTabs.findIndex((t) => t.key === params.get('tab'));
  const activeIndex = wanted >= 0 ? wanted : 0;

  return (
    <div className="p-3" data-testid={testId}>
      <ScreenHeader icon={icon} iconBgColor={iconBgColor} title={title} subtitle={subtitle} />

      <div className="rounded-md border border-gray-200 bg-white p-3 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <TabBar
          className="mb-4"
          tabs={visibleTabs.map((t) => ({ key: t.key, label: t.header, icon: t.leftIcon }))}
          activeKey={visibleTabs[activeIndex]?.key ?? ''}
          onChange={(key) => {
            const next = new URLSearchParams(params);
            next.set('tab', key);
            setParams(next, { replace: true });
          }}
        />
        {/* Only the open tab mounts — switching no longer fires every embedded
            list's queries up front, and the URL keeps the chosen tab. */}
        {visibleTabs[activeIndex]?.element}
      </div>
    </div>
  );
};

export default GroupScreen;
